import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslations } from '@components/context';
import StepForm from '../../StepForm';
import {
    applyVideoInterviewDataToStep,
    getStepIndexForGapDrop,
    insertStepAt,
    mapFlowForSave,
    moveElements,
    sameStepNameConflict,
    reconcileFlowAfterSave,
} from './helper';
import { showConfirmationOverlay } from '@helpers/helpers';
import RecruitmentFlowStepContainer from '@components/RecruitmentFlowEditor/RecruitmentFlowStepContainer';
import '@components/RecruitmentFlowEditor/index.less';
import StructuredList from '@components/core/StructuredList';
import FlowCard from '@components/composites/FlowCard';
import Button from '@components/legacy/core/Button';
import classNames from 'classnames';

const FLOW_STEP_DND_TYPE = 'recruitment_flow_step';
const STEP_PICKER_AT_END = 'end';

export const FlowStepArrow = () => <FlowCard.Connector className="recruitment-flow-step-arrow" />;

/**
 * Render the add-step control shown in the gap below a step.
 * @param {Function} props.renderPicker - called with the button's ref while open; the picker uses it as its anchor
 * @param props.dropRef - when given, makes the gap a drop target for dragged steps
 */
const StepInsertControl = ({ isOpen, label, onToggle, renderPicker, dropRef, isDropTarget = false }) => {
    const buttonRef = useRef(null);

    return (
        <div
            ref={dropRef}
            className={classNames('recruitment-flow-step-insert', {
                'is-open': isOpen,
                'is-drop-target': isDropTarget,
            })}
        >
            <button
                ref={buttonRef}
                type="button"
                className="recruitment-flow-step-insert-btn"
                aria-label={label}
                aria-expanded={isOpen}
                title={label}
                onClick={onToggle}
            >
                <span className="icon-svg-plus" />
            </button>
            {isOpen ? renderPicker(buttonRef) : null}
        </div>
    );
};

/** Render a StepInsertControl that also accepts a dragged step into the gap before `gapIndex`. */
const SortableStepInsertControl = ({ gapIndex, moveStep, ...controlProps }) => {
    const [{ isDropTarget }, drop] = useDrop(
        () => ({
            accept: FLOW_STEP_DND_TYPE,
            canDrop: (item) => getStepIndexForGapDrop(item.index, gapIndex) !== null,
            drop: (item) => {
                const toIndex = getStepIndexForGapDrop(item.index, gapIndex);
                if (toIndex !== null) {
                    moveStep(item.index, toIndex);
                }
            },
            collect: (monitor) => ({ isDropTarget: monitor.isOver() && monitor.canDrop() }),
        }),
        [gapIndex, moveStep],
    );

    return <StepInsertControl {...controlProps} dropRef={drop} isDropTarget={isDropTarget} />;
};

const SortableFlowStepRow = ({ index, moveStep, stepContent, insertControl, showArrow = true }) => {
    const rowRef = useRef(null);
    const dragHandleRef = useRef(null);
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: FLOW_STEP_DND_TYPE,
            item: () => ({ index }),
            collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        }),
        [index],
    );

    const [{ isOver, canDrop }, drop] = useDrop(
        () => ({
            accept: FLOW_STEP_DND_TYPE,
            canDrop: (item) => item.index !== index,
            drop: (item) => {
                if (item.index !== index) {
                    moveStep(item.index, index);
                }
            },
            collect: (monitor) => ({
                isOver: monitor.isOver({ shallow: true }),
                canDrop: monitor.canDrop(),
            }),
        }),
        [index, moveStep],
    );

    drop(rowRef);
    drag(dragHandleRef);

    const stepWithHandle = React.cloneElement(stepContent, {
        dragHandleRef,
        dragging: isDragging,
        dropTarget: isOver && canDrop,
    });

    return (
        <StructuredList.Item
            data-type="step"
            className={classNames({
                'structured-list-item--drop-over': isOver && canDrop,
                'structured-list-item--dragging': isDragging,
            })}
        >
            <div ref={rowRef} className={classNames('recruitment-flow-step')}>
                {stepWithHandle}
            </div>
            {showArrow ? <FlowStepArrow /> : null}
            {insertControl}
        </StructuredList.Item>
    );
};

const DEFAULT_FLOW = {
    name: '',
    steps: [],
};

const FlowForm = ({
    title,
    flow: defaultFlow,
    /** @type {'settings'|'position'} Company flow editor vs job/plan editor (deadline row, etc.) */
    flowStepVariant = 'settings',
    allowDragAndDrop = true,
    onChange,
    metadata,
    ref,
    extendedRender,
    renderStepMeta,
    extraRemoveWarning,
    onOpenVideoStepEditor,
    savedStepIds,
}) => {
    const { t } = useTranslations();
    const [flow, setFlow] = useState(defaultFlow || DEFAULT_FLOW);
    const { max_recruitment_flow_steps } = metadata || {};
    /** Where the step picker inserts: a step index, STEP_PICKER_AT_END, or null while it is closed. */
    const [stepPickerPosition, setStepPickerPosition] = useState(null);
    const addStepAnchorRef = useRef(null);

    const showNameInput = flowStepVariant === 'settings';

    const stepPresets = useMemo(() => {
        const stepIds = new Set(flow?.steps?.map(({ id }) => id));
        return metadata?.steps?.map((step) => ({ ...step, isAlreadyPresent: stepIds.has(step?.id) }));
    }, [metadata?.steps, flow?.steps]);

    const updateStep = useCallback((newStep) => {
        setFlow((prevFlow) => {
            const steps = prevFlow?.steps || [];
            const stepExists = steps.some((step) => sameStepNameConflict(step, newStep));
            if (stepExists) {
                notification(t('Step already exists'), { type: 'warning' });
                return prevFlow;
            }

            const newSteps = steps.map((step) =>
                step.id === newStep.id
                    ? {
                        ...(step || {}),
                        ...(newStep || {}),
                        i18n: { ...(step?.i18n || {}), ...(newStep?.i18n || {}) }
                    }
                    : (step || {})
            );
            const updatedFlow = { ...(prevFlow || {}), steps: newSteps };

            return updatedFlow;
        });
    }, []);

    const addStep = useCallback((newStep) => {
        setFlow((prevFlow) => {
            const steps = prevFlow?.steps || [];
            const index = stepPickerPosition === STEP_PICKER_AT_END ? steps.length : stepPickerPosition;
            return { ...prevFlow, steps: insertStepAt(steps, index, newStep) };
        });
        setStepPickerPosition(null);
    }, [stepPickerPosition]);

    const closeStepForm = useCallback(() => {
        setStepPickerPosition(null);
    }, []);

    const toggleStepPickerAt = useCallback((position) => {
        setStepPickerPosition((current) => (current === position ? null : position));
    }, []);

    useEffect(() => {
        onChange?.(mapFlowForSave(flow));
    }, [flow, onChange]);

    const reconcileAfterFlowSave = useCallback((savedFlow) => {
        if (!savedFlow) return;
        setFlow((prevFlow) => reconcileFlowAfterSave(prevFlow, savedFlow) || prevFlow);
    }, []);

    const mergeVideoInterviewData = useCallback((stepId, videoData) => {
        if (!stepId || !videoData) return;
        setFlow((prevFlow) => ({
            ...prevFlow,
            steps: (prevFlow?.steps || []).map((step) =>
                step.id === stepId ? applyVideoInterviewDataToStep(step, videoData) : step,
            ),
        }));
    }, []);

    const checkStepExistence = useCallback(
        (newStep) => flow?.steps?.some((step) => sameStepNameConflict(step, newStep)),
        [flow?.steps],
    );

    const addPresetStep = useCallback(
        (presetId) => {
            const preset = stepPresets?.find((p) => p.id === presetId);
            if (preset) addStep(preset);
        },
        [stepPresets, addStep],
    );

    const removeStep = useCallback((id) => {
        const overlayId = `remove-flow-step-${String(id).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
        // A consequence the reader would not otherwise see, said in the confirmation they already get
        // rather than in a second dialogue.
        const extraWarning = extraRemoveWarning?.(id);
        showConfirmationOverlay(
            overlayId,
            t('Remove step'),
            extraWarning ? `${t('remove_step_text')}\n\n${extraWarning}` : t('remove_step_text'),
            { label: t('Cancel') },
            {
                label: t('Remove'),
                action: () => {
                    setFlow((prevFlow) => ({ ...prevFlow, steps: (prevFlow?.steps || []).filter((step) => step.id !== id) }));
                },
            },
            false,
        );
    }, [t, extraRemoveWarning]);

    const validateFlow = useCallback(() => {
        let stepsCount = flow?.steps?.length || 0,
            invalids = [];
        const flowName = flow?.name || '';
        if (showNameInput && !String(flowName).trim()) {
            invalids.push(t('invalid_flow_name'));
        }

        if (stepsCount < 2) {
            invalids.push(t('invalid_flow_min_steps'));
        } else if (stepsCount > max_recruitment_flow_steps) {
            invalids.push(t('invalid_flow_max_steps').replace('{}', max_recruitment_flow_steps));
        }

        return invalids;
    }, [max_recruitment_flow_steps, showNameInput, t, flow]);


    useImperativeHandle(ref, () => ({
        getData: () => mapFlowForSave(flow),
        validate: validateFlow,
        mergeVideoInterviewData,
        reconcileAfterFlowSave,
    }), [validateFlow, flow, mergeVideoInterviewData, reconcileAfterFlowSave]);

    const moveStep = useCallback((dragIndex, hoverIndex) => {
        setFlow((prevFlow) => ({
            ...prevFlow,
            steps: moveElements(prevFlow?.steps || [], dragIndex, hoverIndex),
        }));
    }, []);

    const onChangeNameHandler = useCallback((e) => {
        setFlow((prevFlow) => ({ ...prevFlow, name: e.target.value }));
    }, []);

    const renderNameInput = () => {
        return (
            <div className='form-group'>
                <label className='control-label'>
                    {t('recruitment_flows_name')}
                </label>
                <input
                    id="create-flow-name"
                    type="text"
                    className="form-control"
                    value={flow?.name}
                    onChange={onChangeNameHandler}
                />
            </div>
        );
    };

    const renderStepForm = ({ positionBottom, anchorRef }) => (
        <StepForm
            metadata={metadata}
            presets={stepPresets}
            onSubmit={addStep}
            onPresetSelect={addPresetStep}
            onClose={closeStepForm}
            checkStepExistence={checkStepExistence}
            positionBottom={positionBottom}
            anchorRef={anchorRef}
        />
    );

    const renderInsertControl = (gapIndex) => {
        const controlProps = {
            isOpen: stepPickerPosition === gapIndex,
            label: t('add_step'),
            onToggle: () => toggleStepPickerAt(gapIndex),
            renderPicker: (anchorRef) => renderStepForm({ positionBottom: true, anchorRef }),
        };
        return allowDragAndDrop
            ? <SortableStepInsertControl {...controlProps} gapIndex={gapIndex} moveStep={moveStep} />
            : <StepInsertControl {...controlProps} />;
    };

    const renderAddStepButton = () => {
        return (<div className='add-step-container' ref={addStepAnchorRef}>
            <Button
                id='add-step'
                type='dashed-border'
                className='add-step-btn'
                onClick={() => toggleStepPickerAt(STEP_PICKER_AT_END)}
                icon='icon-svg-plus'
                title={t('add_step')}
            />
            {stepPickerPosition === STEP_PICKER_AT_END
                ? renderStepForm({ positionBottom: flow?.steps?.length < 3, anchorRef: addStepAnchorRef })
                : null}
        </div>)
    }

    const recruitmentFlowStepsList = (
        <StructuredList className="recruitment-flow-steps recruitment-flow-steps--insertable">
            {flow?.steps?.map((step, index) => {
                const isLastStep = index === flow.steps.length - 1;
                const flowCard = (
                    <RecruitmentFlowStepContainer
                        key={step.id}
                        step={step}
                        variant={flowStepVariant}
                        className={allowDragAndDrop ? '' : 'recruitment-flow-step'}
                        onChange={updateStep}
                        onRemove={removeStep}
                        metadata={metadata}
                        allowDragAndDrop={allowDragAndDrop}
                        onOpenVideoStepEditor={onOpenVideoStepEditor}
                        savedStepIds={savedStepIds}
                        renderStepMeta={renderStepMeta}
                        body={extendedRender?.(step.id) || null}
                        isFirstStep={index === 0}
                    />
                );

                return allowDragAndDrop ? (
                    <SortableFlowStepRow
                        key={step.id}
                        index={index}
                        moveStep={moveStep}
                        stepContent={flowCard}
                        insertControl={isLastStep ? null : renderInsertControl(index + 1)}
                        showArrow={!isLastStep}
                    />
                ) : (
                    <StructuredList.Item key={step.id} data-type="step">
                        {flowCard}
                        {isLastStep ? null : <FlowStepArrow />}
                        {isLastStep ? null : renderInsertControl(index + 1)}
                    </StructuredList.Item>
                );
            })}
            {renderAddStepButton()}
        </StructuredList>
    );

    return (
        <div id='create-flow-view' className='recruitment-flow-editor'>
            {(title || showNameInput) && <div className='form-heading'>
                <div className='row'>
                    <div className='col-xs-12'>
                        <div className='spacing-top'>
                            {title ? <h3 className='spacing-bottom'>{title}</h3> : null}
                            <div className='row'>
                                <div className='col-xs-12 col-md-6'>
                                    {showNameInput && renderNameInput()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>}
            {allowDragAndDrop ? (
                <DndProvider backend={HTML5Backend}>{recruitmentFlowStepsList}</DndProvider>
            ) : (
                recruitmentFlowStepsList
            )}
        </div>
    );
};
export default FlowForm;
